import {
  ChangeDetectionStrategy,
  Component,
  Input,
  HostListener,
  AfterViewInit,
  inject,
  ViewChild,
  ElementRef,
  signal,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";

import { ClickContext } from "src/app/core/services/click-context.service";
import { environment } from "src/environments/environment";
import { Document } from "../../models/document.model";

@Component({
  selector: "audio-gallery",
  templateUrl: "./gallery.component.html",
  styleUrls: ["./gallery.component.sass"],
  imports: [FaIconComponent],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryComponent implements AfterViewInit {
  private document = inject(DOCUMENT);
  private el = inject(ElementRef);

  @ViewChild("slide") slide!: ElementRef;
  @ViewChild("thumbnails") thumbnails!: ElementRef;

  @Input() sources!: Document[];
  @Input() width = 420;
  @Input() height = 900;

  environment = environment;
  public $active = signal<number>(0);

  protected clickContext = new ClickContext();

  ngAfterViewInit() {
    if (this.document.addEventListener) {
      this.document.addEventListener(
        "fullscreenchange",
        this.fullsceenChangeHandler,
        false,
      );
      this.document.addEventListener(
        "mozfullscreenchange",
        this.fullsceenChangeHandler,
        false,
      );
      this.document.addEventListener(
        "MSFullscreenChange",
        this.fullsceenChangeHandler,
        false,
      );
      this.document.addEventListener(
        "webkitfullscreenchange",
        this.fullsceenChangeHandler,
        false,
      );
    }
  }

  fullsceenChangeHandler = () => {
    if (!this.fullscreenElement()) {
      this.exitFullscreen();
    }
  };

  @HostListener("window:keydown", ["$event"])
  keyEvent(event: KeyboardEvent) {
    console.log(event.key);
    if (event.key === "Escape") {
      return this.exitFullscreen();
    }
    if (event.key === "ArrowRight") {
      return this.next();
    }
    if (event.key === "ArrowLeft") {
      return this.prev();
    }
  }

  handleClick(event: MouseEvent) {
    if (event.detail === 1) {
      // it was a single click
      this.singleClick(event);
    } else if (event.detail === 2) {
      // it was a double click
      this.doubleClick(event);
    }
  }

  singleClick(event: MouseEvent) {
    this.clickContext.clickHandler(() => this.next(event))(event);
  }

  doubleClick(event: MouseEvent) {
    this.clickContext.dblClickHandler(() => this.toggleFullscreen(event))(
      event,
    );
  }

  next(event: MouseEvent | null = null) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.select((this.$active() + 1) % this.sources.length);
  }

  prev(event: MouseEvent | null = null) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.$active() > 0) {
      this.select((this.$active() - 1) % this.sources.length);
    } else {
      this.select(this.sources.length - 1);
    }
  }

  select(selection: number) {
    this.$active.set(selection);
    this.thumbnails?.nativeElement.children[this.$active()].scrollIntoView({
      block: "center",
      container: "nearest",
    });
  }

  toggleFullscreen(event: MouseEvent | null = null) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.fullscreenElement()) {
      this.exitFullscreen();
    } else {
      this.requestFullscreen();
    }
  }

  isImage(source: Document) {
    return this.compareMimeType(source, [
      "image/jpeg",
      "image/webp",
      "image/png",
    ]);
    // return this.compareFileExtension(path, ['jpg', 'JPG', 'jpeg', 'JPEG', 'png', 'PNG']);
  }

  isVideo(source: Document) {
    return this.compareMimeType(source, ["video/mp4", "video/webm"]);
    // return this.compareFileExtension(path, ['mp4', 'avi']);
  }

  protected getFileExtension(filename: string) {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  }

  protected compareFileExtension(filename: string, extensions: string[]) {
    const fileExtension = this.getFileExtension(filename);
    return extensions?.indexOf(fileExtension) !== -1;
  }

  protected compareMimeType(source: Document, mimes: string[]) {
    return !!source && mimes?.indexOf(source["content-type"]) !== -1;
  }

  protected requestFullscreen() {
    // console.log("requestFullscreen");

    this.el.nativeElement.classList.add("fullscreened");

    if (!this.fullscreenEnabled()) {
      // console.error('Fullscreen not enabled on device')
      return;
    }

    if (this.slide.nativeElement.requestFullscreen) {
      this.slide.nativeElement.requestFullscreen();
    } else if (this.slide.nativeElement.webkitRequestFullscreen) {
      this.slide.nativeElement.webkitRequestFullscreen();
    } else if (this.slide.nativeElement.mozRequestFullScreen) {
      this.slide.nativeElement.mozRequestFullScreen();
    } else if (this.slide.nativeElement.msRequestFullscreen) {
      this.slide.nativeElement.msRequestFullscreen();
    }

    // this.fullscreened = true;
  }

  protected exitFullscreen() {
    // console.log("exitFullscreen");

    this.el.nativeElement.classList.remove("fullscreened");

    if (!this.fullscreenElement()) {
      // console.error('No fullscreen element')
      return;
    }

    if (this.document.exitFullscreen) {
      this.document.exitFullscreen();
    }

    // this.fullscreened = false;
  }

  protected fullscreenEnabled() {
    if (
      this.document.fullscreenEnabled /*||
      this.document.webkitFullscreenEnabled ||
      this.document.mozFullScreenEnabled ||
      this.document.msFullscreenEnabled*/
    ) {
      return true;
    } else {
      return false;
    }
  }

  public fullscreenElement(returnElement = false) {
    if (
      this.document.fullscreenElement /*||
      this.document.webkitFullscreenElement ||
      this.document.mozFullScreenElement ||
      this.document.msFullscreenElement*/
    ) {
      if (returnElement) {
        return this.document.fullscreenElement /* ||
          this.document.webkitFullscreenElement ||
          this.document.mozFullScreenElement ||
          this.document.msFullscreenElement*/;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }
}
